// Runtime spine-authority gate. Reads GET /session → identity.spine_authority and exposes canAct(action)
// + disabledReason(action). Controls enable IFF the write will succeed (no enabled→403). When `enforced`
// is false (ENTITLEMENT_ENFORCEMENT flag off) the server sends every action allowed — the UI still trusts
// the envelope, never a local role table (design rule 4 / CLAUDE.md).

import { useQuery } from '@tanstack/react-query';
import { USE_MSW } from '../config';
import { apiGet } from '../api/client';
import { ROUTES } from '../api/routes';
import { SPINE_ACTIONS, type SpineAction, type SpineAuthority } from './vocabulary';

interface SessionEnvelope {
  identity?: { spine_authority?: SpineAuthority };
}

// Dev fixture: everything allowed, unenforced — mirrors the flag-off server (byte-identical legacy gate).
const FIXTURE_AUTHORITY: SpineAuthority = {
  allowed_actions: [...SPINE_ACTIONS] as SpineAction[],
  disabled_reasons: {},
  enforced: false,
};

// Pure gate predicates (no React) so the enforced/unenforced matrix is unit-testable in isolation.
/** When unenforced the server allows everything; when enforced, gate strictly on allowed_actions. */
export function canActWith(authority: SpineAuthority, action: SpineAction): boolean {
  return !authority.enforced || authority.allowed_actions.includes(action);
}
/** The server-supplied deny reason for a disabled control, or undefined when the action is allowed. */
export function denyReasonWith(authority: SpineAuthority, action: SpineAction): string | undefined {
  if (canActWith(authority, action)) return undefined;
  return authority.disabled_reasons[action] ?? 'action_not_allowed';
}

export interface SpineAuthorityApi {
  authority: SpineAuthority;
  isLoading: boolean;
  /** True if the control for this action should be enabled (the write will be accepted). */
  canAct: (action: SpineAction) => boolean;
  /** The server-supplied deny reason for a disabled control, or undefined when allowed. */
  disabledReason: (action: SpineAction) => string | undefined;
}

export function useSpineAuthority(): SpineAuthorityApi {
  const q = useQuery({
    queryKey: ['session', 'spine_authority'],
    staleTime: 60_000,
    queryFn: async (): Promise<SpineAuthority> => {
      if (USE_MSW) return FIXTURE_AUTHORITY;
      const res = await apiGet<SessionEnvelope>(ROUTES.session);
      return res.data.identity?.spine_authority ?? { allowed_actions: [], disabled_reasons: {}, enforced: true };
    },
  });

  const authority = q.data ?? FIXTURE_AUTHORITY;

  return {
    authority,
    isLoading: q.isLoading,
    canAct: (action) => canActWith(authority, action),
    disabledReason: (action) => denyReasonWith(authority, action),
  };
}
